import type { GetUser } from "@redotech/account-common/user";
import { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { toSupportTicketTimelineEventResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/timeline-event/timeline-event-response";
import type { SupportTicketBulkActionNotification } from "@redotech/effect-api-shared/models/support-ticket/support-ticket-bulk-action-notification";
import type { SupportTicketNotification } from "@redotech/effect-api-shared/models/support-ticket/support-ticket-notification";
import { parsePresenceUserId } from "@redotech/effect-api-shared/models/team/presence-notification";
import type {
  PresenceStreamEvent,
  PresenceStreamInitial,
} from "@redotech/effect-api-stream-schemas";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { useSubscribe } from "@redotech/redo-merchant-app-common/stream/stream-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import {
  markDraftSendError,
  markDraftSendingStage,
  markDraftWaiting,
} from "@redotech/redo-support-v2/drafts/support-ticket-draft-send-state";
import {
  clearDraftSendState,
  clearDraftSendStateForThread,
  setDraftSendState,
} from "@redotech/redo-support-v2/drafts/support-ticket-draft-send-state-collection";
import {
  writeMessageLinks,
  writeThreadLinks,
} from "@redotech/redo-support-v2/query/support-ticket-transcript-collections";
import {
  notificationSupportTicketId,
  supportTicketPresenceKey,
} from "@redotech/redo-support-v2/realtime/support-ticket-notifications";
import {
  clearPresence,
  upsertPresence,
} from "@redotech/redo-support-v2/realtime/support-ticket-presence-collection";
import { toast } from "@redotech/redo-web/alert";
import { useQueryClient } from "@tanstack/react-query";
import { memo, useContext } from "react";
import { KnowledgeCaptureContext } from "../../context/knowledge-capture-context";
import {
  reconcileSupportTicketTranscript,
  transcriptQueryKeys,
} from "../db/collections";
import { invalidateSupportTicketListQueriesAfterDelay } from "../invalidate-support-ticket-list-queries";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";
import { sendingDraftIdsForMessageCreated } from "./notification-sync-drafts";

/**
 * NotificationSyncHandlers
 *
 * Registers built-in sync handlers for messages, support tickets, threads, and presence.
 * This component subscribes to notification events and syncs them to TanStack DB collections.
 *
 * Relies on StreamProvider (mounted at root) for the new stream system.
 * Must be placed AFTER GlobalSupportTicketStoreProvider (provides the collections).
 *
 * Handles:
 * - messageCreated: Clear SENDING draft for the thread, insert real message
 * - messageUpdated: Update messages in transcript
 * - supportTicketCreated: Insert new support tickets
 * - supportTicketUpdated: Update ticket status, assignment, priority, read status
 * - threadCreated: Insert new threads
 * - timelineEventCreated: Append timeline events
 * - presenceViewing: Track users currently viewing a support ticket (onInitial + onEvent)
 * - typing: Track users currently typing in a support ticket
 * - presenceCleared: Remove or update presence when a user leaves or stops typing
 */
export const NotificationSyncHandlers = memo(
  function NotificationSyncHandlers() {
    const collections = useGlobalSupportTicketCollections();
    const queryClient = useQueryClient();
    const effectApi = useRequiredContext(EffectApiClientContext);
    const team = useContext(TeamContext);
    const currentUser = useContext(UserContext);
    const { showKnowledgeCapture } = useRequiredContext(
      KnowledgeCaptureContext,
    );

    const invalidateSupportTicketListQueries = useHandler(() =>
      invalidateSupportTicketListQueriesAfterDelay({
        queryClient,
        queryScope: collections.supportTicketsList.queryScope,
      }),
    );

    // Re-index a ticket's transcript after a merge/unmerge touches it.
    //
    // Merging or unmerging moves a thread (and its messages) between the source
    // and destination tickets — the destination gains the source's threads as
    // merged context, and undoing returns them. The transcript collections sync
    // on-demand and are keyed by thread/message id, so a bare collection refetch
    // updates a moved row's data but does NOT re-index it under its new
    // supportTicketId — the useLiveQuery filter (eq(supportTicketId, openTicket))
    // then keeps missing it until a full reload. Reconcile the ticket's
    // authoritative transcript instead (the same path used on open): it pulls the
    // server's view and re-indexes any row cached under a now-merged source, so a
    // merchant who merges from inside the destination ticket sees the merged-in
    // messages slide in live rather than after a manual refresh.
    const reconcileMergeAffectedTranscript = useHandler(
      (affectedSupportTicketId: SupportTicketId) => {
        void reconcileSupportTicketTranscript({
          effectApi,
          threads: collections.threads,
          threadLinks: collections.threadLinks,
          messages: collections.messages,
          messageLinks: collections.messageLinks,
          messagePages: collections.messagePages,
          supportTicketId: affectedSupportTicketId,
        });
      },
    );

    const onSupportTicketNotificationEvent = useHandler(
      (event: SupportTicketNotification) => {
        switch (event._tag) {
          case "messageCreated": {
            const supportTicketId = notificationSupportTicketId(event);
            if (supportTicketId === undefined) {
              break;
            }
            const sentDraftIds = sendingDraftIdsForMessageCreated({
              drafts: collections.drafts.values(),
              supportTicketId,
              channelThreadId: event.message.channelThreadId,
            });

            // Drop the sent draft locally (the server deletes it as part of the
            // send). Do NOT removeQueries/invalidateQueries the shared drafts
            // key: every per-ticket subset shares it, so wiping drops every
            // loaded ticket's drafts, and the refetch can race the server-side
            // deletion and resurrect the just-sent draft.
            for (const draftId of sentDraftIds) {
              collections.drafts.utils.writeDelete(draftId);
            }

            // Clear the send overlay by thread even when the draft row isn't
            // loaded (the merchant switched tickets mid-send, so sentDraftIds is
            // empty) — otherwise its "waiting"/"sending" card is stranded.
            clearDraftSendStateForThread(
              collections.draftSendState,
              event.message.channelThreadId,
            );

            if (collections.messages.isReady()) {
              collections.messages.utils.writeUpsert(event.message);
              writeMessageLinks(collections.messageLinks, supportTicketId, [
                { messageId: event.message.id },
              ]);
            }

            // The SSE payload always carries `attachments: []` because the
            // server publishes the notification before the async attachment
            // sync has run (see support-ticket-timeline-projection.ts
            // toFrontEndObject() — the default-arg path). The optimistic
            // upsert above shows the message immediately; this refetch
            // hydrates attachments through findAllByTopicId's SQL join so
            // images/videos/GIFs render without a page reload.
            void queryClient.invalidateQueries({
              queryKey: transcriptQueryKeys.messages(),
            });

            if (
              collections.threads.isReady() &&
              !collections.threads.has(event.message.channelThreadId.toString())
            ) {
              void queryClient.invalidateQueries({
                queryKey: transcriptQueryKeys.threads(),
              });
            }

            // Refresh the inbox list so an inbound message on an existing
            // ticket bumps it to the top and updates unread counts. Without
            // this the merchant has to reload to see new activity arrive.
            invalidateSupportTicketListQueries();
            break;
          }

          case "supportTicketCreated": {
            if (collections.supportTicketsList.collection.isReady()) {
              collections.supportTicketsList.collection.utils.writeUpsert(
                event.supportTicket,
              );
            }
            invalidateSupportTicketListQueries();
            break;
          }

          case "supportTicketUpdated": {
            if (!collections.supportTicketsList.collection.isReady()) {
              break;
            }

            const supportTicketId = notificationSupportTicketId(event);
            if (supportTicketId === undefined) {
              break;
            }
            let update: Partial<SupportTicketListItemResponse> & {
              id: SupportTicketId;
            } = { id: supportTicketId };

            switch (event.updateType) {
              case "status_changed":
                update = {
                  ...update,
                  ...(event.newStatus !== undefined && {
                    status: event.newStatus,
                  }),
                  ...(event.newSnoozedUntil !== undefined && {
                    snoozedUntil: event.newSnoozedUntil,
                  }),
                };
                break;
              case "assigned":
                update = { ...update, mongoAssigneeId: event.newAssignee };
                break;
              case "priority_changed":
                update = { ...update, priority: event.newPriority };
                break;
              case "read":
                update = { ...update, readAt: new Date() };
                break;
              case "tag_added": {
                if (event.tagId === undefined) {
                  break;
                }
                const current =
                  collections.supportTicketsList.collection.get(
                    supportTicketId,
                  );
                if (!current || current.tagIds.includes(event.tagId)) {
                  break;
                }
                update = {
                  ...update,
                  tagIds: [...current.tagIds, event.tagId],
                };
                break;
              }
              case "tag_removed": {
                if (event.tagId === undefined) {
                  break;
                }
                const current =
                  collections.supportTicketsList.collection.get(
                    supportTicketId,
                  );
                if (!current || !current.tagIds.includes(event.tagId)) {
                  break;
                }
                update = {
                  ...update,
                  tagIds: current.tagIds.filter((id) => id !== event.tagId),
                };
                break;
              }
            }

            collections.supportTicketsList.collection.utils.writeUpdate(update);
            invalidateSupportTicketListQueries();
            break;
          }

          case "threadCreated": {
            if (!collections.threads.isReady()) {
              break;
            }
            const supportTicketId = notificationSupportTicketId(event);
            if (supportTicketId === undefined) {
              break;
            }
            if (collections.threads.has(event.thread.id.toString())) {
              collections.threads.utils.writeUpsert(event.thread);
            } else {
              collections.threads.utils.writeInsert(event.thread);
            }
            writeThreadLinks(collections.threadLinks, supportTicketId, [
              { threadId: event.thread.id },
            ]);
            break;
          }

          case "messageUpdated": {
            if (!collections.messages.isReady()) {
              break;
            }
            const supportTicketId = notificationSupportTicketId(event);
            if (supportTicketId === undefined) {
              break;
            }
            collections.messages.utils.writeUpsert(event.message);
            writeMessageLinks(collections.messageLinks, supportTicketId, [
              { messageId: event.message.id },
            ]);
            // Same rationale as messageCreated: the SSE payload's
            // attachments field is built from toFrontEndObject() with no
            // attachments arg, so refetch to hydrate.
            void queryClient.invalidateQueries({
              queryKey: transcriptQueryKeys.messages(),
            });
            break;
          }

          case "timelineEventCreated": {
            const timelineEvent = toSupportTicketTimelineEventResponse(
              event.event,
            );
            if (collections.timelineEvents.isReady()) {
              collections.timelineEvents.utils.writeUpsert(timelineEvent);
            }
            if (
              timelineEvent.eventType === "support_ticket_merged" ||
              timelineEvent.eventType === "support_ticket_merge_undone"
            ) {
              // Merge/unmerge emits one timeline event per side (source and
              // destination), so reconciling this event's own ticket covers
              // both across the pair without redundant fetches per merged
              // source.
              reconcileMergeAffectedTranscript(timelineEvent.supportTicketId);
            }
            break;
          }

          case "knowledgeSuggestionsGenerated": {
            // Only the closer sees it; everyone else gets the notification and
            // ignores it, same as conversationMentioned below.
            const closerId = event.notifyMongoUserId;
            if (
              !currentUser?._id ||
              closerId === null ||
              MongoUserId.toString(closerId) !== currentUser._id
            ) {
              break;
            }
            showKnowledgeCapture({
              conversationId: String(event.supportTicketId),
              suggestionIds: event.suggestionIds,
              preview: event.preview,
            });
            break;
          }
          case "conversationMentioned": {
            const currentUserId = currentUser?._id;
            if (!currentUserId) {
              break;
            }
            const isCurrentUserMentioned = event.mentionedMongoUserIds.some(
              (id) => MongoUserId.toString(id) === currentUserId,
            );
            if (!isCurrentUserMentioned) {
              break;
            }
            if (
              event.mentionedByMongoUserId &&
              MongoUserId.toString(event.mentionedByMongoUserId) ===
                currentUserId
            ) {
              break;
            }
            const mentionedByHexId = event.mentionedByMongoUserId
              ? MongoUserId.toString(event.mentionedByMongoUserId)
              : undefined;
            const senderName =
              event.mentionedByName ??
              (mentionedByHexId && team
                ? // prettier-ignore
                  (
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                    team.users.find((u) => {
                      const user = u.user as GetUser | undefined; // eslint-disable-line @typescript-eslint/no-unsafe-type-assertion
                      return user?._id === mentionedByHexId;
                    })?.user as GetUser | undefined
                  )?.name
                : undefined);
            const title = senderName
              ? `${senderName} mentioned you`
              : "You were mentioned";
            const message = event.messagePreview
              ? `${title}: ${event.messagePreview}`
              : title;
            toast(message, { variant: "info" });
            break;
          }

          case "draftSendStateChanged": {
            // Update the overlay by draftId regardless of whether the draft row
            // is loaded (the merchant may have switched tickets mid-send), or the
            // originating ticket is stranded on a stale card. The row write is
            // best-effort, applied only when the row is loaded.
            const draftKey = String(event.draftId);
            const rowLoaded = collections.drafts.has(draftKey);
            switch (event.stage) {
              case "sending":
                setDraftSendState(
                  collections.draftSendState,
                  event.draftId,
                  event.threadId,
                  "sending",
                  event.sendAt,
                );
                if (rowLoaded) {
                  collections.drafts.utils.writeUpdate(
                    markDraftSendingStage(event.draftId, event.sendAt),
                  );
                }
                break;
              case "error":
                clearDraftSendState(collections.draftSendState, event.draftId);
                if (rowLoaded) {
                  collections.drafts.utils.writeUpdate(
                    markDraftSendError(
                      event.draftId,
                      event.userFacingErrorMessage,
                    ),
                  );
                }
                toast(
                  event.userFacingErrorMessage ??
                    "Message failed to send — the draft was restored",
                  { variant: "error" },
                );
                break;
              case "waiting":
                if (event.sendAt !== undefined) {
                  setDraftSendState(
                    collections.draftSendState,
                    event.draftId,
                    event.threadId,
                    "waiting",
                    event.sendAt,
                  );
                  if (rowLoaded) {
                    collections.drafts.utils.writeUpdate(
                      markDraftWaiting(event.draftId, event.sendAt),
                    );
                  }
                }
                break;
              case "sent":
                // Completion normally comes via messageCreated; clear here too
                // in case a "sent" stage is emitted directly.
                clearDraftSendState(collections.draftSendState, event.draftId);
                break;
            }
            break;
          }
        }
      },
    );

    // Notice/Redis pub-sub does not buffer events for absent subscribers, so
    // anything the server publishes while the SSE connection is briefly down
    // — including the brief window between the initial REST fetch and SSE
    // subscribing — is lost. The provider auto-reconnects + resubscribes
    // but never replays missed events. Invalidate the transcript + inbox
    // on every ack (initial AND reconnect) so the merchant doesn't need
    // to refresh to surface missed messages, attachments, or thread/ticket
    // updates. The first-ack refetch is redundant with the initial REST
    // load but is a cheap insurance policy against missed messages that
    // arrived in the subscribe-window.
    const onSupportTicketResubscribe = useHandler(() => {
      void queryClient.invalidateQueries({ queryKey: transcriptQueryKeys.all });
      invalidateSupportTicketListQueries();
    });

    useSubscribe({
      channel: "support-ticket-notifications",
      onEvent: onSupportTicketNotificationEvent,
      onSubscribed: onSupportTicketResubscribe,
    });

    // A completed merge is a second moment the inbox and destination transcript
    // catch up. The merge itself does publish per-ticket notifications — the
    // merged-away source gets a `supportTicketUpdated`, and both sides get a
    // `timelineEventCreated` that the `timelineEventCreated` handler above
    // reconciles — but the bulk-action workflow's completion is also a reliable
    // account-independent signal, so drive a catch-up from it too: re-index the
    // destination transcript so a merchant viewing the new head sees the merged
    // messages live, and refresh the inbox so the head row reflects the merged
    // last message and counts. The destination notification's reconcile and this
    // one are idempotent, so running both is harmless.
    const onBulkSupportTicketActionEvent = useHandler(
      (event: SupportTicketBulkActionNotification) => {
        if (
          event._tag !== "bulkSupportTicketActionProgress" ||
          event.status !== "completed" ||
          event.action._tag !== "mergeSupportTickets"
        ) {
          return;
        }
        // Fetch the destination's authoritative transcript and re-index it.
        // The server returns the moved threads/messages under the destination,
        // so this re-indexes them even when they were already loaded this
        // session under a now-merged source ticket — no need to know the source
        // ids, which the completion notification does not carry.
        void reconcileSupportTicketTranscript({
          effectApi,
          threads: collections.threads,
          threadLinks: collections.threadLinks,
          messages: collections.messages,
          messageLinks: collections.messageLinks,
          messagePages: collections.messagePages,
          supportTicketId: SupportTicketId.make(
            event.action.destinationSupportTicketId,
          ),
        });
        invalidateSupportTicketListQueries();
      },
    );

    useSubscribe({
      channel: "support-ticket-bulk-actions",
      onEvent: onBulkSupportTicketActionEvent,
    });

    const onPresenceInitial = useHandler((snapshot: PresenceStreamInitial) => {
      if (!collections.presence.isReady()) {
        return;
      }
      for (const event of snapshot) {
        // SSE delivers userId as a raw string. Brand it so the collection's
        // typed userId (MongoUserId | "Customer") stays honest. Unknown
        // shapes (malformed legacy data) are dropped rather than surfaced
        // as raw text.
        const userId = parsePresenceUserId(event.userId);
        if (userId === undefined) {
          continue;
        }
        const supportTicketId = notificationSupportTicketId(event);
        if (supportTicketId === undefined) {
          continue;
        }
        upsertPresence(collections.presence, {
          id: supportTicketPresenceKey(event),
          supportTicketId,
          userId,
          isViewing: true,
          isTyping: false,
        });
      }
    });

    const onPresenceEvent = useHandler((event: PresenceStreamEvent) => {
      if (!collections.presence.isReady()) {
        return;
      }

      const userId = parsePresenceUserId(event.userId);
      if (userId === undefined) {
        return;
      }

      switch (event._tag) {
        case "presenceViewing": {
          const supportTicketId = notificationSupportTicketId(event);
          if (supportTicketId === undefined) {
            break;
          }
          upsertPresence(collections.presence, {
            id: supportTicketPresenceKey(event),
            supportTicketId,
            userId,
            isViewing: true,
            isTyping: false,
          });
          break;
        }

        case "typing": {
          const supportTicketId = notificationSupportTicketId(event);
          if (supportTicketId === undefined) {
            break;
          }
          upsertPresence(collections.presence, {
            id: supportTicketPresenceKey(event),
            supportTicketId,
            userId,
            isViewing: true,
            isTyping: true,
          });
          break;
        }

        case "presenceCleared": {
          const key = supportTicketPresenceKey(event);
          if (event.activity === "typing") {
            const supportTicketId = notificationSupportTicketId(event);
            if (supportTicketId === undefined) {
              break;
            }
            upsertPresence(collections.presence, {
              id: key,
              supportTicketId,
              userId,
              isViewing: true,
              isTyping: false,
            });
          } else {
            clearPresence(collections.presence, key);
          }
          break;
        }
      }
    });

    useSubscribe({
      channel: "presence",
      onInitial: onPresenceInitial,
      onEvent: onPresenceEvent,
    });

    return null;
  },
);
